/**
 * V6-010 砺锋台 V2 — 经验与等级系统 + 段位辅助
 *
 * 等级公式：Lv.N 无上限，指数递增
 *   expNeededForLevel(N) = Σ(i=1 to N-1) 5 × 2^(i-1)
 *   即 5/10/20/40/80/160/320...
 *
 * 段位：复用 climb-manifest 4 阶段军衔（战士/军官/校官/将帅）
 *
 * @see design/v6-sharpen/gdd-v6-010-sharpen.md §9 §21.1
 * @date 2026-07-19
 * @updated 2026-07-20 — 新增段位辅助函数
 */

/** 计算达到 Lv.N 所需的累计经验 */
export function expNeededForLevel(level: number): number {
  if (level <= 1) return 0;
  let total = 0;
  for (let i = 1; i < level; i++) {
    total += 5 * Math.pow(2, i - 1);  // 5, 10, 20, 40, 80, 160...
  }
  return total;
}

/** 根据经验推导等级 */
export function levelFromExp(exp: number): number {
  let level = 1;
  while (exp >= expNeededForLevel(level + 1)) level++;
  return level;
}

/** 当前等级的进度信息（用于经验条） */
export interface LevelProgress {
  currentLevel: number;
  currentLevelExp: number;     // 进入当前等级时的累计 EXP
  nextLevelExp: number;        // 升到下一级所需累计 EXP
  progress: number;            // 0-1，当前等级内进度
  expInCurrentLevel: number;   // 当前等级内已得 EXP
  expNeededForNext: number;    // 当前等级升级所需 EXP
  expToNext: number;           // 距离升级还差多少 EXP
}

/** 计算当前等级进度 */
export function levelProgress(exp: number): LevelProgress {
  const currentLevel = levelFromExp(exp);
  const currentLevelExp = expNeededForLevel(currentLevel);
  const nextLevelExp = expNeededForLevel(currentLevel + 1);
  const expNeededForNext = nextLevelExp - currentLevelExp;
  const expInCurrentLevel = exp - currentLevelExp;
  const progress = expNeededForNext > 0 ? expInCurrentLevel / expNeededForNext : 0;
  return {
    currentLevel,
    currentLevelExp,
    nextLevelExp,
    progress: Math.min(1, Math.max(0, progress)),
    expInCurrentLevel,
    expNeededForNext,
    expToNext: nextLevelExp - exp,
  };
}

/** Lv.N → K1-K12 勋章索引（0-11） */
export function levelToMedalIndex(level: number): number {
  // Lv.1-2 → K1, Lv.3-4 → K2, Lv.5-6 → K3, ... Lv.23-24 → K12
  const idx = Math.floor((level - 1) / 2);
  return Math.min(11, Math.max(0, idx));
}

/** 胜率计算 */
export function winRate(wins: number, total: number): number {
  if (total === 0) return 0;
  return wins / total;
}

// ── 段位辅助函数（提案 1，2026-07-20）──

import type { RankId, RankConfig, RankProgressEntry } from "./types";
import { RANK_CONFIGS, RANK_PROMOTION_THRESHOLD } from "./types";

/** 获取军衔配置 */
export function getRankConfig(rankId: RankId): RankConfig {
  return RANK_CONFIGS.find((r) => r.id === rankId)!;
}

/** 获取下一个军衔（参谋用） */
export function getNextRank(rankId: RankId): RankId | null {
  const idx = RANK_CONFIGS.findIndex((r) => r.id === rankId);
  if (idx < 0 || idx >= RANK_CONFIGS.length - 1) return null;
  return RANK_CONFIGS[idx + 1].id;
}

/** 计算本段通关进度（0-1） */
export function rankProgressPct(progress: RankProgressEntry): number {
  if (progress.total === 0) return 0;
  return progress.cleared / progress.total;
}

/** 判定是否可以晋升到下一段 */
export function canPromote(entry: RankProgressEntry): boolean {
  return rankProgressPct(entry) >= RANK_PROMOTION_THRESHOLD;
}

/** 生成解锁条件文本 */
export function unlockConditionText(
  rankId: RankId,
  entry: RankProgressEntry
): string {
  const needed = Math.max(1, Math.ceil(entry.total * RANK_PROMOTION_THRESHOLD - entry.cleared));
  const config = getRankConfig(rankId);
  return `解锁条件：赢 ${needed} 场${config.name}段关`;
}

/** 段位修正胜率（mock 阶段） */
export function rankAdjustedWinRate(
  playerRankIndex: number,
  levelRankIndex: number
): number {
  const gap = levelRankIndex - playerRankIndex; // 正=越级挑战
  return Math.max(0.1, 0.5 - gap * 0.2);
  // 同段: 50% | 越1段: 30% | 越2段: 15% | 越3段: 10%
}
